import {Select} from 'antd';
import {FormInstance} from 'antd/es/form';
import React, {FC, ReactElement, ReactText} from 'react';

import {
    IFieldMeta,
    getReferenceResolverFunction,
    IAdditionalInputOptions,
    IAdditionalOptions,
    IReferenceParamKey,
} from 'entities/metadata';
import {
    TEntity, TEntityValue,
} from 'modules/data';
import {
    IFilterEntityData,
    IFormEntityData,
    IReferenceResolverParams,
} from 'modules/data/data-types';
import { ERequestType } from 'shared/types/requests';
import {getPopupContainer, isObject} from 'shared/utils';

import {EFormMode} from '../../form-types';
import {CustomSelectSpinner} from '../custom-select/components/custom-select-spinner';
import {useReferenceSelectAdditionalOptions} from './hooks/reference-select-additional-options-hook';
import {useReferenceSelectConfig} from './hooks/reference-select-config-hook';
import {useReferenceSelectOptions} from './hooks/reference-select-options-hook';
import {SearchableDropdown} from './searchable-dropdown';
import {TagRenderer} from './tag-renderer';
import {getReferenceParamsValues, getReferenceResolver} from './utils/reference-select.utils';

import './reference-select.less';

export const normalizeValueForViewing = (
    currentValue: any,
) => {
    let result: string | string[] = '';
    const referenceResolver = getReferenceResolverFunction('meaning', 'id', 'description', 'lookupCode');

    if (typeof currentValue === 'string') {
        result = currentValue
            .split(/,\s*/)
            .join('; ');
    } else if (Array.isArray(currentValue)) {
        result = currentValue
            .map(record => referenceResolver(record)?.label || record)
            .join('; ');
    } else if (isObject(currentValue)) {
        result = (referenceResolver(currentValue as Record<string, any>)?.label);
    }

    return result;
};

export interface IAdditionalLoadOptions {
    onNotCreateKey?: boolean;
    displayFieldKey?: string;
    dependentInputKey?: string;
    additionalInputOptions?: IAdditionalInputOptions[];
    isSetFilter?: boolean;
    requestFieldKey?: string;
    requestKey?: string;
    requestType?: ERequestType;
}

export interface IReferenceSelectProps {
    field: IFieldMeta;
    entityName: string;
    parentEntityName?: string;
    referenceUrl: string;
    additionalOptions?: IAdditionalOptions;
    isFilterable?: boolean;
    multipleMode?: boolean;
    path?: IReferenceResolverParams['path'];
    data?: TEntity[];
    shouldSimpleDisplayValue?: boolean;
    value?: TEntity;
    onChange?: (value: any) => void;
    disabled?: boolean;
    referenceParamsKeys?: IReferenceParamKey[];
    referenceParamsDefault?: Record<string, any>;
    isDependsOnMainFilter?: string;
    filter?: IFilterEntityData;
    formData?: IFormEntityData;
    stringStructure?: string;
    onNotCreateKey?: boolean;
    displayFieldKey?: string;
    requestFieldKey?: string;
    dependentInputKey?: string;
    additionalInputOptions?: IAdditionalInputOptions[];
    isSetFilter?: boolean;
    fixedDropdown?: boolean;
    useContext?: boolean;
    useFirstOptionAsDefaultValue?: boolean;
    form?: FormInstance;
    defaultValue?: string;
    defaultValueByRule?: string;
    hideSearch?: boolean;
    isClearable?: boolean;
    formMode?: EFormMode;
    areProgramSetProgramsUnique?: boolean;
    isDisabledWhenSingle?: boolean;
}

export const ReferenceSelect: FC<IReferenceSelectProps> = ({
    referenceUrl,
    field,
    value,
    isFilterable,
    multipleMode,
    shouldSimpleDisplayValue,
    disabled,
    path,
    stringStructure,
    fixedDropdown = true,
    displayFieldKey,
    requestFieldKey,
    form,
    referenceParamsKeys,
    defaultValue,
    hideSearch,
    defaultValueByRule,
    isClearable,
    formMode,
    formData,
    areProgramSetProgramsUnique,
    isDisabledWhenSingle = false,
    ...props
}: IReferenceSelectProps) => {
    const referenceParamsValues = getReferenceParamsValues(referenceParamsKeys, form, formData);
    const referenceResolver = getReferenceResolver({referenceUrl, path}, stringStructure);

    const mode = multipleMode ? 'multiple' : undefined;
    const {
        options,
        getSelectedValuesFromOptions,
        valueReferenceSelect,
        normalizeValue,
        handleChange,
        defaultValueOption,
        loading,
        placeholder,
    } = useReferenceSelectOptions({
        ...props,
        referenceResolver,
        referenceUrl,
        value,
        displayFieldKey,
        requestFieldKey,
        referenceParamsValues,
        defaultValue,
        form,
        field,
        defaultValueByRule,
        isDisabledWhenSingle,
    });

    useReferenceSelectAdditionalOptions({
        ...props,
        key: field.key,
        form,
        formValue: value,
        referenceUrl,
        formMode,
        formData,
        areProgramSetProgramsUnique,
    });

    const {
        dropdownOpen,
        setDropdownOpen,
        renderOptions,
        suffixIcon,
        dropdownInputValue,
        setDropdownInputValue,
        setDropdownSelectedIcon,
    } = useReferenceSelectConfig(options, disabled, multipleMode, isFilterable, displayFieldKey, field.key);

    const filterOption = (inputValue: string, option: TEntityValue) => {
        const {children} = option as Record<string, any> || {};
        return children?.toLowerCase().includes(inputValue.toLowerCase());
    };

    if (shouldSimpleDisplayValue) {
        return (
            <>
                {normalizeValueForViewing(
                    getSelectedValuesFromOptions(normalizeValue(value) || ''),
                )}
            </>
        );
    }

    const isSingleValue = isDisabledWhenSingle && options.length === 1;

    return (
        <Select
            {...props}
            showArrow
            allowClear={isClearable}
            showSearch={!hideSearch}
            className="select-field"
            mode={mode}
            filterOption={filterOption}
            onChange={handleChange}
            loading={loading}
            disabled={loading || disabled || isSingleValue}
            value={(() => {
                if (loading) return <CustomSelectSpinner /> as unknown as ReactText;
                return valueReferenceSelect() || defaultValueOption();
            })()}
            open={dropdownOpen}
            tagRender={TagRenderer}
            onDropdownVisibleChange={v => setDropdownOpen(v)}
            placeholder={placeholder}
            // чтобы dropdown сохранял позицию
            getPopupContainer={getPopupContainer(fixedDropdown, true)}
            menuItemSelectedIcon={mode && setDropdownSelectedIcon}
            suffixIcon={suffixIcon}
            dropdownRender={isFilterable && multipleMode ? (menu: ReactElement) => (
                <SearchableDropdown
                    menu={menu}
                    inputValue={dropdownInputValue}
                    setInputValue={setDropdownInputValue}
                />
            ) : undefined}
        >
            {renderOptions}
        </Select>
    );
};
